import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Client } from '../../types';
import './ClientForm.css';

interface ClientFormProps {
  onSuccess?: () => void;
  editClient?: Client | null;
  onCancel?: () => void;
}

const formatPhone000_0000_0000 = (value: string) => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 11);
  const part1 = digitsOnly.slice(0, 3);
  const part2 = digitsOnly.slice(3, 7);
  const part3 = digitsOnly.slice(7, 11);

  if (digitsOnly.length <= 3) return part1;
  if (digitsOnly.length <= 7) return `${part1}-${part2}`;
  return `${part1}-${part2}-${part3}`;
};

const ClientForm: React.FC<ClientFormProps> = ({ onSuccess, editClient = null, onCancel }) => {
  const isEditMode = !!editClient;
  const [clientName, setClientName] = useState(editClient?.client_name || '');
  const [email, setEmail] = useState(editClient?.email || '');
  const [phone, setPhone] = useState(editClient?.phone || '');
  const [contactName, setContactName] = useState(editClient?.contact_name || '');
  const [address, setAddress] = useState(editClient?.address || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Update form when editClient changes
  useEffect(() => {
    if (editClient) {
      setClientName(editClient.client_name || '');
      setEmail(editClient.email || '');
      setPhone(formatPhone000_0000_0000(editClient.phone || ''));
      setContactName(editClient.contact_name || '');
      setAddress(editClient.address || '');
    }
  }, [editClient]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const payload: any = {
        client_name: clientName,
        email: email || null,
        phone: phone || null,
        contact_name: contactName || null,
        address: address || null,
      };

      if (isEditMode && editClient) {
        // Update existing client
        await api.put<Client>(`/clients/${editClient.id}`, payload);
        setSuccess('Company updated successfully!');
      } else {
        // Create new client
        await api.post<Client>('/clients', payload);
        setSuccess('Company created successfully!');
        setClientName('');
        setEmail('');
        setPhone('');
        setContactName('');
        setAddress('');
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} company. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="client-form-container">
      <h3>{isEditMode ? 'Edit Company' : 'Create New Company'}</h3>
      <form onSubmit={handleSubmit} className="client-form">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="form-group">
          <label htmlFor="client_name">Company Name</label>
          <input
            type="text"
            id="client_name"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            required
            placeholder="Enter company name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="client_email">Email</label>
          <input
            type="email"
            id="client_email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter client email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="client_phone">Phone</label>
          <input
            type="text"
            id="client_phone"
            value={phone}
            onChange={(e) => setPhone(formatPhone000_0000_0000(e.target.value))}
            inputMode="numeric"
            autoComplete="tel"
            placeholder="000-0000-0000"
            maxLength={13}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contact_name">Contact Name</label>
          <input
            type="text"
            id="contact_name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Enter contact name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="client_address">Address</label>
          <input
            type="text"
            id="client_address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Enter address"
          />
        </div>

        {isEditMode && editClient && (
          <div className="form-group">
            <label htmlFor="uniq_id">Unique ID</label>
            <input
              type="text"
              id="uniq_id"
              value={editClient.uniq_id || 'N/A'}
              disabled
              style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
              placeholder="Unique ID cannot be changed"
            />
            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Unique ID cannot be edited
            </small>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          {isEditMode && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          )}
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Company' : 'Create Company')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;
